
























import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";

// Enums
import { ProductTypes } from "@/data/enums/products";
import { DataModules } from "@/store/modules/data";
import { SortableItems } from "@/data/enums/sortableItems";

// Types
import {
  IProductAttribute,
  IProductOption,
  IProductCategory,
  ISelectedProduct,
  IProduct
} from "@/models/products";
import { IState } from "@/store";

@Component({
  components: {
    "multilevel-categories-selections": () =>
      import(
        "@/components/app/admin/catalogue/products/multilevelCategories/multilevelCategoriesSelections.vue"
      )
  },
  computed: {
    ...mapState({
      attachedProducts(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.dataModule,
          scope: this.attachedProductsScope
        });
      },
      attachedProductsScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/attachedProductsScope`](
          this.productId,
          this.productType
        );
      },
      rootCategories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.categoriesModule,
          scope: this.rootCategoriesScope
        });
      },
      rootCategoriesScope(state: IState, getters) {
        return getters[`data/${this.categoriesModule}/rootCategoriesScope`](
          this.brandId,
          [this.productType]
        );
      },

      productScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/scope`](this.productId);
      }
    })
  }
})
export default class MultilevelProductSettings extends Mixins(
  RequestPromises,
  BrandsStore
) {
  // Getters
  public attachedProducts!: IProductOption[] | IProductAttribute[];
  public attachedProductsScope!: string;
  public rootCategoriesScope!: string;
  public rootCategories!: IProductCategory[];
  public productScope!: string;

  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ required: true })
  public productType!: ProductTypes;

  @Prop({ default: false })
  public isDisabled!: boolean;

  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;

  public dataModule: DataModules = DataModules.CATALOGUE;
  public categoriesModule: DataModules = DataModules.CATALOGUE_CATEGORIES;

  public selectedProducts: ISelectedProduct[] = [];
  public selectedCategories: any[] = [];

  public async created() {
    if (this.contentIsAvailable) {
      await this.loadData();

      this.$bus.$on("refreshAttachedSettings", () => {
        this.isLoading = true;
        this.reloadData();
      });
    }
  }

  public async loadData() {
    try {
      await this.getRootCategories();
      await this.getProductSettings(this.isReloading);
      await this.refreshData();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getRootCategories(forceReload = false) {
    return this.$store.dispatch(`data/${this.categoriesModule}/list`, {
      productTypes: [this.productType],
      scope: this.rootCategoriesScope,
      params: {
        order: "order",
        limit: 0,
        "filter[level]": 1,
        brand_id: this.brandId
      },
      vm: this,
      ignoreStored: forceReload
    });
  }

  public async reorder(movedElement: any) {
    const movedProductIds =
      this.$_.get(movedElement, "itemMoved.type") === SortableItems.ITEM
        ? [this.$_.get(movedElement, "itemMoved.id")]
        : this.$_.map(
            this.$_.get(movedElement, "itemMoved.items"),
            item => item.id
          );

    let newOrder = this.$_.filter(
      this.$_.map(this.selectedProducts, selectedProduct => selectedProduct.id),
      productId => {
        return (
          this.$_.findIndex(movedProductIds, itemId => itemId === productId) ===
          -1
        );
      }
    );

    if (this.$_.get(movedElement, "itemMoved.type") === SortableItems.ITEM) {
      newOrder.splice(
        this.findNewIndex(
          movedElement.newIndex,
          this.$_.get(movedElement, "itemMoved.category_id")
        ),
        0,
        this.$_.get(movedElement, "itemMoved.id")
      );
    } else {
      const categoriesIds = this.$_.filter(
        this.$_.map(this.selectedCategories, category => category.id),
        categoryId => categoryId !== this.$_.get(movedElement, "itemMoved.id")
      );

      categoriesIds.splice(
        movedElement.newIndex,
        0,
        this.$_.get(movedElement, "itemMoved.id")
      );

      newOrder = this.$_.flatten(
        this.$_.map(categoriesIds, categoryId => {
          return this.$_.map(
            this.$_.filter(
              this.$_.orderBy(this.attachedProducts, ["attached_order"]),
              ["category_id", categoryId]
            ),
            attachedProduct => attachedProduct.id
          );
        })
      );
    }

    await this.reorderProductSettings(newOrder);
    await this.getProductSettings(true);
    await this.refreshData();

    return { ignoreRefresh: true };
  }

  public findNewIndex(newIndex, categoryId) {
    return (
      this.$_.findIndex(this.selectedProducts, ["category_id", categoryId]) +
      newIndex
    );
  }

  public reorderProductSettings(newOrder) {
    return this.$store.dispatch(
      `data/${this.dataModule}/reorderProductSettings`,
      {
        id: this.productId,
        settingType: this.productType,
        settingIds: newOrder
      }
    );
  }

  public get getKey() {
    return this.$_.map(
      this.attachedProducts,
      attachedProduct => attachedProduct.id
    ).join("_");
  }

  public getProductSettings(ignoreStore = false) {
    return this.$store.dispatch(`data/${this.dataModule}/listSettings`, {
      id: this.productId,
      productType: this.productType,
      scope: this.attachedProductsScope,
      vm: this,
      ignoreStored: ignoreStore,
      params: this.$_.merge(
        {
          limit: 0,
          order: "attached_order",
          brand_id: this.brandId
        },
        this.isOption ? { with: "prices" } : {}
      )
    });
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${this.dataModule}/get`, {
      id: this.productId,
      scope: this.productScope,
      vm: this,
      ignoreStored: ignoreStored
    });
  }

  public detachElement(elementId: IProduct["id"], elementType: string) {
    this.selectedProductsChanged(
      this.$_.filter(this.selectedProducts, selectedProduct => {
        return elementType === SortableItems.ITEM
          ? selectedProduct.id !== elementId
          : selectedProduct.category_id !== elementId;
      })
    );
  }

  public get getSettingName() {
    return this.isOption ? "option" : "attribute";
  }

  public get isOption() {
    return this.productType === ProductTypes.PRODUCT_OPTION;
  }

  public async selectedProductsChanged(products: ISelectedProduct[]) {
    this.isProcessing = true;

    this.selectedProducts = this.$_.flatten(
      this.$_.map(this.$_.uniqBy(products, "category_id"), category =>
        this.$_.filter(products, ["category_id", category.category_id])
      )
    );

    try {
      await this.attachSettings();
      await this.detachSettings();

      await this.reorderProductSettings(
        this.$_.map(
          this.selectedProducts,
          selectedProduct => selectedProduct.id
        )
      );

      await this.getProductSettings(true);
      await this.refreshData();
      await this.getProductData(true);

      this.$store.dispatch("toast/show", {
        message: this.$t("_.object_successfully_updated", {
          object: this.$t(
            `_.product_${this.isOption ? "options" : "attributes"}`
          )
        })
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }

    this.isProcessing = false;
  }

  public refreshData() {
    this.selectedProducts = this.$_.map(
      this.$_.orderBy(this.attachedProducts, ["attached_order"]),
      product => {
        return this.$_.merge(
          this.$_.pick(product, ["id", "name", "category_id"]),
          { order: this.$_.get(product, "attached_order") }
        );
      }
    );
    this.selectedCategories = this.getAttachedCategories;
  }

  public get getAttachedCategories() {
    return this.$_.map(
      this.$_.uniqBy(
        this.$_.orderBy(this.attachedProducts, ["attached_order"]),
        "category_id"
      ),
      product => {
        return {
          id: this.$_.get(product, "category_id"),
          name: this.$_.get(product, "category.name"),
          type: SortableItems.CATEGORY,
          isOpened: true,
          isLoading: false,
          isFullyLoaded: true,
          items: this.mapProducts(
            this.$_.filter(this.attachedProducts, [
              "category_id",
              this.$_.get(product, "category_id")
            ])
          )
        };
      }
    );
  }

  public mapProducts(products: any[]) {
    return this.$_.map(
      this.$_.orderBy(products, ["attached_order", "asc"]),
      (product: IProduct) => {
        return this.$_.merge(
          this.$_.pick(product, ["id", "name", "category_id"]),
          {
            type: SortableItems.ITEM,
            order: this.$_.get(product, "attached_order"),
            labels: this.$_.concat(
              this.$_.get(product, "available_for_sales")
                ? []
                : [
                    {
                      title: this.i18n.inactive,
                      type: "is-dark",
                      description: this.i18n.inactiveDescription
                    }
                  ],
              this.$_.get(product, "clients_can_order")
                ? []
                : [
                    {
                      title: this.i18n.hidden,
                      type: "is-grey",
                      description: this.i18n.hiddenDescription
                    }
                  ]
            )
          }
        );
      }
    );
  }

  public get i18n() {
    return {
      hidden: this.$t("_.hidden"),
      hiddenDescription: this.$t("_sentence.hidden_desc"),
      inactive: this.$t("_.inactive"),
      inactiveDescription: this.$t("_sentence.inactive_desc")
    };
  }

  public attachSettings() {
    const toAttachProducts: IProduct["id"][] = this.$_.differenceWith(
      this.$_.map(this.selectedProducts, (selectedProduct: any) => {
        return selectedProduct.id;
      }),
      this.$_.map(this.attachedProducts, (product: any) => {
        return product.id;
      }),
      this.$_.isEqual
    );

    if (toAttachProducts.length) {
      const data: object = {};
      data[
        `${this.isOption ? "options" : "products_attributes"}`
      ] = this.$_.map(toAttachProducts, productId => {
        const parameter: any = {};
        parameter[`${this.isOption ? "option" : "attribute"}_id`] = productId;
        return parameter;
      });

      return this.$store.dispatch(
        `data/${this.dataModule}/attachProductSettings`,
        {
          id: this.productId,
          settingType: this.isOption ? "options" : "products_attributes",
          data
        }
      );
    } else {
      return Promise.resolve();
    }
  }

  public detachSettings() {
    const toDetachProducts: IProduct["id"][] = this.$_.differenceWith(
      this.$_.map(this.attachedProducts, (product: any) => {
        return product.id;
      }),
      this.$_.map(this.selectedProducts, (selectedProduct: any) => {
        return selectedProduct.id;
      }),
      this.$_.isEqual
    );

    if (toDetachProducts.length) {
      return new Promise((resolve, reject) => {
        Promise.all(
          this.$_.map(toDetachProducts, settingId => {
            return this.$store.dispatch(
              `data/${this.dataModule}/detachProductSettings`,
              {
                id: this.productId,
                settingType: this.isOption ? "options" : "products_attributes",
                settingId: settingId
              }
            );
          })
        )
          .then(() => {
            resolve(true);
          })
          .catch(error => {
            reject(error);
          });
      });
    } else {
      return Promise.resolve();
    }
  }

  public canManageProduct(type: string) {
    if (type === "attach") {
      switch (this.productType) {
        case ProductTypes.PRODUCT_OPTION:
          return this.$userCan("attach_product_options");
        case ProductTypes.PRODUCT_ATTRIBUTE:
          return this.$userCan("attach_product_attributes");
        default:
          return false;
      }
    } else {
      switch (this.productType) {
        case ProductTypes.PRODUCT_OPTION:
          return this.$userCan("detach_product_option");
        case ProductTypes.PRODUCT_ATTRIBUTE:
          return this.$userCan("detach_product_attribute");
        default:
          return false;
      }
    }
  }

  public get contentIsAvailable() {
    return this.$userCan("staff_get_product") && this.isOption
      ? this.$userCan("list_product_options_categories") &&
          this.$userCan("list_product_options")
      : this.$userCan("list_product_attributes_categories") &&
          this.$userCan("list_product_attributes");
  }

  public get guardFunctionalities() {
    return this.isOption
      ? [
          "staff_get_product",
          "list_product_options",
          "list_product_options_categories"
        ]
      : [
          "staff_get_product",
          "list_product_attributes",
          "list_product_attributes_categories"
        ];
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.dataModule,
      scope: this.attachedProductsScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: this.categoriesModule,
      scope: this.rootCategoriesScope,
      vm: this
    });

    this.$store.dispatch("data/bin", {
      storeModule: this.dataModule,
      scope: this.productScope,
      vm: this
    });

    this.$bus.$off("refreshAttachedSettings");
  }
}
